import React, { Fragment } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import { BOOKING_URL, BOOKING_ENABLED } from '../../config/booking';
import { trackEvent } from '../../utils/analytics';
import imgHero from '../../images/indicator/bli-hero.png';
import imgLayers from '../../images/indicator/bli-layers.png';
import imgLevels from '../../images/indicator/bli-levels.png';
import imgPanel from '../../images/indicator/bli-panel.png';

const ClickHandler = () => window.scrollTo(10, 0);
const PINE_URL = '/downloads/buildlab-institutional-indicator.pine';

const onDownload = () => trackEvent('indicator_download', { indicator: 'BuildLab Institutional Indicator' });

const navy = '#020842';
const blue = '#0044EB';
const gold = '#C77F12';

// Each level, explained plainly
const LEVELS = [
    { t: 'Daily VWAP + σ bands', c: '#E08A2E', d: 'Volume-weighted average price, re-anchored each session, with ±1/2/3 standard-deviation bands. Above VWAP = buyers in control; ±2σ = stretched and prone to mean-revert on range days.' },
    { t: 'Floor-trader pivots', c: blue, d: 'Classic PP, R1–R3, and S1–S3 from the prior day’s high/low/close — the day’s equilibrium and the reaction targets price gravitates toward.' },
    { t: 'Prior Day / Week H&L', c: '#8A5BD0', d: 'Where stops cluster. A break-and-hold = continuation; a spike-through that fails = a liquidity grab and likely reversal.' },
    { t: 'Initial Balance', c: '#2FB8CC', d: 'The first hour’s high/low. Break-and-hold above signals a potential trend day with range extension; holding inside favors rotation.' },
    { t: '15-min Opening Range', c: '#3CBE6E', d: 'The day’s first decision level off the NY open. Break-and-hold sets early momentum; failure to break either side points to a range day.' },
];

// The leading / context layers
const LAYERS = [
    { t: 'NYSE TICK', d: 'Net up-ticking vs down-ticking stocks right now — breadth that leads index price.' },
    { t: 'Advancers / Decliners', d: 'How broad the participation is. Positive supports rallies; negative warns a move lacks backing.' },
    { t: 'VIX trend', d: 'Falling volatility = risk-on; rising = risk-off. A simple regime read.' },
    { t: 'ES / NQ alignment', d: 'Whether the partner index agrees. Divergence is your cue to lower conviction.' },
    { t: 'Market resilience', d: 'A cap-weighted mega-cap basket scored vs open, prior close, and the half-gap — is big-cap money defending the move?' },
    { t: 'Breadth divergence', d: 'Price makes a new extreme but breadth doesn’t — a leading warning the move is losing support.' },
];

const INSTALL = [
    'Open TradingView and click the Pine Editor tab at the bottom of any chart.',
    'Open the downloaded .pine file in a text editor, select all, and copy it.',
    'Paste it into the Pine Editor (replace the starter template), then click Save and name it.',
    'Click “Add to chart.” Open the settings gear to toggle levels, the breadth layer, and the panel.',
    'Best on index futures (NQ/MNQ, ES/MES) on a 1–15 min intraday timeframe during the regular session.',
];

const IndicatorPage = () => {
    usePageSeo({
        title: 'Free Institutional Indicator for TradingView',
        description: 'Free TradingView indicator (Pine Script v6): daily VWAP with sigma bands, floor pivots, opening range, initial balance, and a market-internals decision panel.',
        keywords: 'free TradingView indicator, Pine Script v6, VWAP sigma bands, floor pivots indicator, opening range, initial balance, NYSE TICK breadth, market internals, NQ ES futures indicator, BUILDLAB',
    });

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'BUILDLAB Institutional Indicator'} pagesub={'Free Download'} pageTop={'Custom Software'} />

                {/* HERO + DOWNLOAD */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="mb-4" style={{ borderRadius: 18, overflow: 'hidden', boxShadow: '0 24px 60px rgba(2,8,66,.18)' }}>
                            <img src={imgHero} alt="BUILDLAB Institutional Indicator for TradingView" style={{ width: '100%', display: 'block' }} />
                        </div>
                        <div className="row align-items-center">
                            <div className="col-lg-7">
                                <h2 className="mb-3">A transparent read on institutional market structure — free</h2>
                                <p>
                                    The BUILDLAB Institutional Indicator is a public, price-only analog to the paid gamma and
                                    order-flow tools traders pay monthly for. It plots the levels that actually move index futures —
                                    daily VWAP with sigma bands, floor pivots, prior day/week highs and lows, the initial balance, and
                                    the opening range — then layers in market internals and a plain-English read of the stack.
                                </p>
                                <p>
                                    No paid feed, no black box, no subscription. Every line is computed from price and volume you can
                                    see. Download the full Pine Script v6 source, drop it into TradingView, and make it your own.
                                </p>
                            </div>
                            <div className="col-lg-5 mt-4 mt-lg-0">
                                <div style={{ background: navy, borderRadius: 16, padding: '30px 32px', color: '#fff' }}>
                                    <div style={{ fontSize: '.78rem', fontWeight: 700, letterSpacing: '.08em', color: '#F3A338' }}>FREE · PINE SCRIPT v6</div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 800, margin: '6px 0 4px', lineHeight: 1.2 }}>Download the full source</div>
                                    <p style={{ fontSize: '.86rem', opacity: .85, marginBottom: 18 }}>
                                        The complete .pine file — open it, read it, edit it. Yours to keep.
                                    </p>
                                    <a href={PINE_URL} download onClick={onDownload} className="btn btn-primary w-100" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Download the Pine Script">↓ Download the Pine Script</span>
                                    </a>
                                    <p style={{ fontSize: '.72rem', opacity: .7, marginTop: 14, marginBottom: 0 }}>
                                        Educational and informational only — not financial advice, and not a recommendation to buy or
                                        sell any security.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* HOW IT WORKS */}
                <section className="section_space">
                    <div className="container">
                        <div className="text-center" style={{ maxWidth: 760, margin: '0 auto 30px' }}>
                            <span style={{ fontSize: '.74rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: blue }}>How it works</span>
                            <h2 className="mt-2">Four layers stack into one bias</h2>
                            <p className="text-muted">Each layer confirms or warns against the others — so you’re never reading a single line in isolation.</p>
                        </div>
                        <img src={imgLayers} alt="How the BUILDLAB Institutional Indicator reads the tape — four layers" className="mb-2" style={{ width: '100%', borderRadius: 16, border: '1px solid var(--bs-border-color)' }} />
                    </div>
                </section>

                {/* LEVEL STACK */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-6 mb-4 mb-lg-0">
                                <img src={imgLevels} alt="The level stack: VWAP, pivots, prior day levels, initial balance, opening range" style={{ width: '100%', borderRadius: 16, border: '1px solid var(--bs-border-color)' }} />
                            </div>
                            <div className="col-lg-6">
                                <span style={{ fontSize: '.74rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: blue }}>The level stack</span>
                                <h2 className="mt-2 mb-3">The lines that move index futures</h2>
                                {LEVELS.map((l) => (
                                    <div key={l.t} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                                        <span style={{ flex: '0 0 auto', width: 12, height: 12, borderRadius: 3, background: l.c, marginTop: 5 }}></span>
                                        <div>
                                            <div style={{ fontWeight: 700, color: navy }}>{l.t}</div>
                                            <div style={{ fontSize: '.88rem' }}>{l.d}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* LEADING LAYER */}
                <section className="section_space">
                    <div className="container">
                        <div className="text-center" style={{ maxWidth: 760, margin: '0 auto 30px' }}>
                            <span style={{ fontSize: '.74rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: blue }}>The leading layer</span>
                            <h2 className="mt-2">Breadth & internals that lead price</h2>
                            <p className="text-muted">Price levels tell you where. Internals tell you whether the move has support behind it.</p>
                        </div>
                        <div className="row">
                            {LAYERS.map((x) => (
                                <div className="col-lg-4 col-md-6 mb-4" key={x.t}>
                                    <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 14, padding: '22px 24px', height: '100%' }}>
                                        <h3 className="h6 mb-1" style={{ color: navy }}>{x.t}</h3>
                                        <p style={{ fontSize: '.88rem', margin: 0 }}>{x.d}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* DECISION PANEL */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-6 order-lg-2 mb-4 mb-lg-0">
                                <img src={imgPanel} alt="The BUILDLAB Institutional Indicator decision panel: levels, internals, bias, and a plain-English call" style={{ width: '100%', borderRadius: 16, border: '1px solid var(--bs-border-color)' }} />
                            </div>
                            <div className="col-lg-6 order-lg-1">
                                <span style={{ fontSize: '.74rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: blue }}>The decision panel</span>
                                <h2 className="mt-2 mb-3">It tells you the read — and when to wait</h2>
                                <p>
                                    The merged panel pulls every level, internal, and bias signal into one place, then ends with a
                                    plain-English recommendation: <strong>wait, entry zone, at resistance, or extended</strong>. It counts
                                    how many lines align (the conviction), checks whether breadth confirms, and refuses to say
                                    &ldquo;enter&rdquo; unless price is at a low-risk spot.
                                </p>
                                <p>
                                    On-chart action tags mark the exact prices it references — your dip, your break, your stop, your
                                    target — and optional alerts fire when a high-conviction stack or a breadth divergence forms.
                                </p>
                                <p style={{ fontWeight: 600, color: gold }}>
                                    Decision support — not a signal to follow blindly. You stay in control of the trade.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* INSTALL */}
                <section className="section_space">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-5 mb-4 mb-lg-0">
                                <span style={{ fontSize: '.74rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: blue }}>Get started</span>
                                <h2 className="mt-2 mb-3">Install it in two minutes</h2>
                                <a href={PINE_URL} download onClick={onDownload} className="btn btn-primary">
                                    <span className="btn_label" data-text="Download the Pine Script">↓ Download the Pine Script</span>
                                </a>
                            </div>
                            <div className="col-lg-7">
                                <ol style={{ paddingLeft: 0, listStyle: 'none', counterReset: 'step' }}>
                                    {INSTALL.map((s, i) => (
                                        <li key={i} style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
                                            <span style={{ flex: '0 0 auto', width: 30, height: 30, borderRadius: '50%', background: 'var(--bs-primary-bg-subtle, #e7eeff)', color: blue, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>{i + 1}</span>
                                            <span style={{ fontSize: '.92rem', paddingTop: 4 }}>{s}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>

                        <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 14, padding: '22px 26px', marginTop: 18 }}>
                            <h3 className="h6 mb-2" style={{ color: navy }}>Want a custom version, or your own indicator built?</h3>
                            <p style={{ fontSize: '.92rem', marginBottom: 16 }}>
                                This is one example of what BUILDLAB builds. We develop custom TradingView indicators, screeners,
                                dashboards, alerts, and strategy backtests — plus conversions from MQL, ThinkScript, and EasyLanguage to Pine v6.
                            </p>
                            <div className="d-flex flex-wrap gap-2">
                                <Link to="/custom-software" onClick={ClickHandler} className="btn btn-outline-dark" style={{ padding: '11px 24px', fontWeight: 600 }}>
                                    Custom software &amp; solutions
                                </Link>
                                {BOOKING_ENABLED && (
                                    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('book_consult', { tool: 'Institutional Indicator' })} className="btn btn-primary">
                                        <span className="btn_label" data-text="Book a build consult">Book a build consult</span>
                                    </a>
                                )}
                            </div>
                        </div>

                        <p className="small text-muted mt-4" style={{ maxWidth: 920 }}>
                            <strong>Disclaimer.</strong> The BUILDLAB Institutional Indicator is provided free, for educational and
                            informational purposes only. It is not financial, investment, or trading advice, and is not a recommendation
                            or solicitation to buy or sell any security or derivative. Trading futures and other leveraged products
                            involves substantial risk of loss. Past behavior of any level or signal does not guarantee future results.
                            You are solely responsible for your own trading decisions.
                        </p>
                    </div>
                </section>

                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default IndicatorPage;
